import React, { useState } from 'react';
import { getCurrentUser } from '@aws-amplify/auth';
import * as registrationService from '../../services/registration/registrationService';

const RegistrationDataTester = ({ registrationId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);

  const addResult = (test, status, message, data = null) => {
    const result = {
      test,
      status, // 'success', 'error', 'info'
      message,
      data,
      timestamp: new Date().toLocaleTimeString()
    };
    setTestResults(prev => [...prev, result]);
    console.log(`[${result.timestamp}] ${test}: ${status.toUpperCase()} - ${message}`, data);
  };

  const runTests = async () => {
    if (!registrationId) {
      addResult('Setup', 'error', 'No registration ID provided');
      return;
    }

    setIsRunning(true);
    setTestResults([]);

    try {
      // Test 1: Get current user
      addResult('Auth', 'info', 'Getting current user...');
      const user = await getCurrentUser();
      if (!user?.userId) {
        addResult('Auth', 'error', 'No authenticated user found');
        return;
      }
      addResult('Auth', 'success', `Found user: ${user.userId}`);

      // Test 2: Test participant addition
      addResult('Participant', 'info', 'Testing participant addition...');
      try {
        const testParticipant = {
          firstName: 'Test',
          lastName: 'Dancer',
          dateOfBirth: '2000-01-01',
          gender: 'Other',
          workshopLevel: 'Beginner',
          options: {
            workshopLevel: 'Beginner',
            competition: false
          }
        };

        const participantResult = await registrationService.addParticipantToRegistration(
          registrationId,
          testParticipant,
          'dancer'
        );

        if (participantResult) {
          addResult('Participant', 'success', `Added test participant: ${participantResult}`, participantResult);
          
          // Test 3: Update the participant
          addResult('Participant Update', 'info', 'Testing participant update...');
          try {
            const updateResult = await registrationService.updateRegistrationParticipant(
              registrationId,
              participantResult,
              { workshopLevel: 'Intermediate' }
            );
            addResult('Participant Update', 'success', 'Updated participant workshop level', updateResult);
          } catch (updateError) {
            addResult('Participant Update', 'error', updateError.message, updateError);
          }

          // Test 4: Remove the test participant
          addResult('Participant Remove', 'info', 'Cleaning up test participant...');
          try {
            await registrationService.removeParticipantFromRegistration(registrationId, participantResult);
            addResult('Participant Remove', 'success', 'Removed test participant');
          } catch (removeError) {
            addResult('Participant Remove', 'error', removeError.message, removeError);
          }
        } else {
          addResult('Participant', 'error', 'Failed to add participant - no result returned');
        }
      } catch (participantError) {
        addResult('Participant', 'error', participantError.message, participantError);
      }

      // Test 5: Test routine addition (if routines exist)
      addResult('Routine', 'info', 'Testing routine addition...');
      try {
        // Use a mock routine ID for testing
        const testRoutineId = 'test-routine-123';
        const routineOptions = {
          style: 'Hip Hop',
          type: 'Small Group',
          typeLabel: 'Small Group',
          averageAge: 15,
          ageGroup: 'Teen'
        };

        const routineResult = await registrationService.addRoutineToRegistration(
          registrationId,
          testRoutineId,
          routineOptions
        );

        if (routineResult) {
          addResult('Routine', 'success', `Added test routine: ${routineResult}`, routineResult);
          
          // Test 6: Remove the test routine
          addResult('Routine Remove', 'info', 'Cleaning up test routine...');
          try {
            await registrationService.removeRoutineFromRegistration(registrationId, routineResult);
            addResult('Routine Remove', 'success', 'Removed test routine');
          } catch (removeError) {
            addResult('Routine Remove', 'error', removeError.message, removeError);
          }
        } else {
          addResult('Routine', 'error', 'Failed to add routine - no result returned');
        }
      } catch (routineError) {
        addResult('Routine', 'error', routineError.message, routineError);
      }

      // Test 7: Test registration data fetch
      addResult('Data Fetch', 'info', 'Testing data fetching...');
      try {
        const participants = await registrationService.fetchRegistrationParticipants(registrationId);
        const routines = await registrationService.loadRegistrationRoutines(registrationId);
        
        addResult('Data Fetch', 'success', `Fetched ${participants?.dancers?.length || 0} dancers, ${participants?.teachers?.length || 0} teachers, ${participants?.observers?.length || 0} observers, ${routines?.length || 0} routines`);
      } catch (fetchError) {
        addResult('Data Fetch', 'error', fetchError.message, fetchError);
      }

    } catch (error) {
      addResult('Test Suite', 'error', error.message, error);
    } finally {
      setIsRunning(false);
    }
  };

  if (!registrationId) {
    return null;
  }

  return (
    <div className="fixed bottom-20 right-4 z-50">
      {!isVisible ? (
        <button
          onClick={() => setIsVisible(true)}
          className="bg-green-600 text-white p-3 rounded-full shadow-lg hover:bg-green-700 transition-colors"
          title="Open Registration Data Tester"
        >
          🧪
        </button>
      ) : (
        <div className="bg-white border border-gray-300 rounded-lg shadow-xl w-96 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="bg-green-600 text-white px-4 py-2 flex justify-between items-center">
            <h3 className="font-medium text-sm">Registration Tester</h3>
            <div className="flex gap-2">
              <button
                onClick={runTests}
                disabled={isRunning}
                className="bg-green-500 hover:bg-green-400 px-2 py-1 rounded text-xs"
                title="Run Tests"
              >
                {isRunning ? '⏳' : '▶️'}
              </button>
              <button
                onClick={() => setTestResults([])}
                className="bg-green-500 hover:bg-green-400 px-2 py-1 rounded text-xs"
                title="Clear Results"
              >
                🗑️
              </button>
              <button
                onClick={() => setIsVisible(false)}
                className="bg-green-500 hover:bg-green-400 px-2 py-1 rounded text-xs"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-80">
            <div className="text-xs text-gray-500 mb-3">
              Registration ID: {registrationId}
            </div>

            {testResults.length === 0 && !isRunning && (
              <div className="text-center text-gray-500 py-8">
                Click the play button to run tests
              </div>
            )}

            {isRunning && (
              <div className="text-center text-blue-600 py-4">
                Running tests...
              </div>
            )}

            <div className="space-y-2">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-2 rounded text-xs ${
                    result.status === 'success' ? 'bg-green-100 text-green-800' :
                    result.status === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-blue-100 text-blue-800'
                  }`}
                >
                  <div className="font-medium">
                    {result.status === 'success' ? '✅' : result.status === 'error' ? '❌' : 'ℹ️'} {result.test}
                  </div>
                  <div className="mt-1">{result.message}</div>
                  <div className="text-xs text-gray-500 mt-1">{result.timestamp}</div>
                  {result.data && (
                    <details className="mt-1">
                      <summary className="cursor-pointer">Data</summary>
                      <pre className="text-xs bg-gray-50 p-1 rounded mt-1 overflow-auto">
                        {JSON.stringify(result.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationDataTester;


























